import { useState, useEffect, useRef } from "react";
import { Photo } from "../types/photo";
import { useNavigate } from "react-router-dom";
import { useModal } from "../contexts/ModalContext";
import LeafletMap from "../components/LeafletMap";
import PhotoItem, { PhotoItemHandle } from "./Photo";
import { getAddressByGeopoints } from "../services/geocodeService";
import { updatePhotoDetails, getPhotoPredictionStatus } from "../services/photoService";
import { Utils } from "../utilities/Utils";
import { useAlbum } from "../contexts/AlbumContext";

import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { MdVerified } from "react-icons/md";
import { VscUnverified } from "react-icons/vsc";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { AiOutlineQuestionCircle } from "react-icons/ai";

interface FormInput {
  title: string | null;
  description: string | null;
  address: string | null;
  addressJson: string | null;
}
interface FormError {
  title?: string | null;
  description?: string | null;
}
type Props = {
  photoItem: Photo;
  onClose?: () => void;
};
const LoadingSpinner = () => (
  <AiOutlineLoading3Quarters className="animate-spin inline-block h-5 w-5 text-white" />
);

const ReadonlyText: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div
      className="pl-2 text-accentAlt border-gray-300 bg-gray-200"
      style={{
        borderWidth: "3px 4px 3px 5px",
        borderRadius: "95% 4% 92% 5%/4% 95% 6% 95%",
        wordWrap: "break-word"
      }}
    >
      {children}
    </div>
  );
};

const PhotoDetail: React.FC<Props> = ({ photoItem, onClose }) => {
  const { openModal } = useModal();
  const navigate = useNavigate();
  const { photos, updatePhoto } = useAlbum();
  const [photo, setPhoto] = useState<Photo>();
  const [submitted, setSubmitted] = useState(false);
  const photoItemRef = useRef<PhotoItemHandle>(null);
  const [formInput, setFormInput] = useState<FormInput>({
    title: null,
    description: null,
    address: null,
    addressJson: null
  });
  const [formError, setFormError] = useState<FormError>({
    title: null,
    description: null
  });
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [addressLoading, setAddressLoading] = useState<boolean>(false);

  const fetchAddress = async ({ lat, long }: { lat: number, long: number }) => {
    try {
      setAddressLoading(true);
      const data = await getAddressByGeopoints(lat, long);

      if (data) {
        let addr = data.display_name;
        let addrJson = JSON.stringify(data);
        setFormInput((prev) => ({
          ...prev,
          address: addr,
          addressJson: addrJson,
        }));
        setPhoto((prev) => prev ? { ...prev, address: addr } : prev);
      }
    } catch (error) {
      console.error("Failed to fetch address:", error);
    } finally {
      setAddressLoading(false);
    }
  };

  useEffect(() => {
    setPhoto(() => {
      setFormInput((prev) => ({
        ...prev,
        title: photoItem.title,
        description: photoItem.description
      }));
      return photoItem;
    });
    if (!photoItem.address && photoItem.latitudeDecValue && photoItem.longitudeDecValue) {
      fetchAddress({ lat: photoItem.latitudeDecValue, long: photoItem.longitudeDecValue });
    }
  }, []);

  const togglePrediction = () => {
    photoItemRef.current?.togglePrediction();
  };

  const handleUserInput = (name: string, value: string) => {
    setFormInput((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const serverValidationResult = (
    errorsList: { code: string; description: string }[]
  ): FormError => {
    const errors: FormError = {};
    errorsList.forEach(({ code, description }) => {
      if (code.includes("IMAGE_TITLE")) errors.title = description;
      if (code.includes("IMAGE_DESC")) errors.description = description;
    });
    return errors;
  };

  const StatusLabel = ({ icon, label }: { icon: any, label: any }) => (
    <div className="flex flex-row">
      <div className="w-full text-center mb-1 px-2 py-1 text-xs text-white bg-gray-800 rounded-md shadow-md">
        <span>{icon} {label}</span>
      </div>
    </div>
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitted) return;
    if (addressLoading) {
      alert("Please wait for the address to load.");
      return;
    }
    setSubmitted(true);
    setError("");

    try {
      if (photo) {
        const jsonPayload: Record<string, string | null> = {};

        if (formInput.title !== photo.title) {
          jsonPayload.title = formInput.title;
        }
        if (formInput.description !== photo.description) {
          jsonPayload.description = formInput.description;
        }
        if (formInput.address && formInput.addressJson) {
          jsonPayload.address = formInput.address;
          jsonPayload.addressJson = formInput.addressJson;
        }
        const hasUpdates = Object.keys(jsonPayload).length > 0;

        if (hasUpdates) {
          console.log(jsonPayload);
          const updated = await updatePhotoDetails(photo.photoId, jsonPayload);
          // merge changes with current photo
          const updatedPhoto: Photo = { ...photo, ...jsonPayload };
          setPhoto(updatedPhoto);
          updatePhoto(updatedPhoto); // context
          setSuccessMsg("Updated successfully!");
        } else {
          alert("No update.");
        }
      }
    } catch (data: any) {
      if (Array.isArray(data.errors)) {
        const apiErrors = serverValidationResult(data.errors);
        setFormError(apiErrors);
      } else {
        setError("Update failed. Please try again later.");
      }
    } finally {
      setSubmitted(false);
    }
  };

  return (
    <form className="custom-form" onSubmit={handleSubmit}>
      {photo &&
        <div className="md:flex">
          {/* Photo preview */}
          <div className="md:w-1/4 w-full flex items-center justify-center">
            {photo.src && <PhotoItem ref={photoItemRef} photo={photo} />}
          </div>

          {/* Map */}
          {photo.latitudeDecValue && photo.longitudeDecValue && (
            <div className="md:w-2/4 w-full relative">
              <LeafletMap
                mode="upload"
                lat={photo.latitudeDecValue ?? 0}
                lng={photo.longitudeDecValue ?? 0}
                draggable={true}
                zoom={12}
              />
            </div>
          )}

          {/* Metadata form */}
          <div className="md:w-1/4 w-full p-8 flex flex-col">
            {/* Still being processed by ML modal */}
            {getPhotoPredictionStatus(photo) === 'processing' && (
              <StatusLabel icon={<VscUnverified className="text-yellow-400 inline-block" />} label={"Analyzing Image"} />
            )}
            {/* Has high score prediction result */}
            {getPhotoPredictionStatus(photo) === 'verified' && (
              <div>
                <StatusLabel icon={<MdVerified className="text-primary inline-block" />} label={"Koala Detected"} />
                <button className="custom-btn w-full" onClick={(e) => { e.preventDefault(); togglePrediction() }}>
                  Show/Hide predicted koala position
                </button>
              </div>
            )}
            {/* Has low score prediction result */}
            {getPhotoPredictionStatus(photo) === 'uncertain' && (
              <div>
                <StatusLabel icon={<AiOutlineQuestionCircle className="text-yellow-400 inline-block" />} label={"Uncertain"} />
                <button className="custom-btn w-full" onClick={(e) => { e.preventDefault(); togglePrediction() }}>
                  Show/Hide predicted koala position
                </button>
              </div>
            )}
            {/* No prediction result */}
            {getPhotoPredictionStatus(photo) === 'not-found' && (
              <StatusLabel icon={<AiOutlineCloseCircle className="text-negative inline-block" />} label={"No Koala Detected"} />
            )}

            {photo.fileName &&
              <div className="field-wrapper">
                <div className="text-sm font-semibold text-primary">
                  File name
                </div>
                <ReadonlyText>{photo.fileName}</ReadonlyText>
              </div>
            }
            {photo.uploadTime &&
              <div className="field-wrapper">
                <div className="text-sm font-semibold text-primary">
                  Upload time
                </div>
                <ReadonlyText>{Utils.formatDate(photo.uploadTime)}</ReadonlyText>
              </div>
            }
            {photo.timestamp &&
              <div className="field-wrapper">
                <div className="text-sm font-semibold text-primary">
                  Observation time
                </div>
                <ReadonlyText>{Utils.formatDate(photo.timestamp)}</ReadonlyText>
              </div>
            }

            {photo.address &&
              <div className="field-wrapper">
                <div className="text-sm font-semibold text-primary">
                  Address
                </div>
                <ReadonlyText>{photo.address}</ReadonlyText>
              </div>
            }

            <div className="field-wrapper">
              <label htmlFor="title">Title (Optional)</label>
              <input id="title" name="title" type="text" value={String(formInput.title ?? "")}
                onChange={(e) => handleUserInput(e.target.name, e.target.value)} />
              {formError.title && (
                <p className="error-message">{formError.title}</p>
              )}
            </div>

            <div className="field-wrapper">
              <label htmlFor="description">Description (Optional)</label>
              <textarea id="description" name="description"
                onChange={(e) => handleUserInput(e.target.name, e.target.value)}
                value={String(formInput.description ?? "")}>
              </textarea>
              {formError.description && (
                <p className="error-message">{formError.description}</p>
              )}
            </div>

            <button type="submit"
              disabled={submitted} className="custom-btn w-full">
              {submitted ?
                <span><LoadingSpinner /> Processing...</span> : <span>Update Details</span>}

            </button>
            {error && <p className="error-message">{error}</p>}
            {successMsg && <p className="success-message">{successMsg}</p>}
          </div>
        </div>
      }


    </form>
  );
};

export default PhotoDetail;